import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Clock, FileText, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { OvertimeRequest, OvertimeStatus } from "@/types";
import { getStatusColor } from "@/utils/statusUtils";
import { OvertimeStatus as OvertimeStatusEnum } from "@/types/enums";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Extended type for requests with joined reason text
interface OvertimeRequestWithJustificationText extends OvertimeRequest {
  justification_text?: string;
}

interface RequestDetailProps {
  request: OvertimeRequestWithJustificationText | null;
}

export function RequestDetail({ request }: RequestDetailProps) {
  if (!request) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Overtime Request</CardTitle>
          <CardDescription>Details of the overtime request</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Not found</AlertTitle>
            <AlertDescription>
              Overtime request not found.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      // Parse date string manually to avoid timezone issues
      const [year, month, day] = dateString.split(/[-T]/);
      if (year && month && day) {
        // Create date in local timezone to prevent day shifts
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return format(date, 'MMM d, yyyy');
      }
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    try {
      return format(new Date(`1970-01-01T${timeString}`), 'h:mm a');
    } catch {
      return timeString;
    }
  };

  // Check if this is a VP approval request that's still pending
  const isPendingVpApproval = request.vp_approval_required && request.status === OvertimeStatusEnum.PENDING;
  const displayStatus = isPendingVpApproval ? OvertimeStatusEnum.PENDING_VP_APPROVAL : request.status;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overtime Request</CardTitle>
        <CardDescription>Details of the overtime request</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Employee</p>
          <p className="text-sm text-muted-foreground">
            <User className="mr-2 inline-block h-4 w-4" />
            {request.employee_name}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Department</p>
          <p className="text-sm text-muted-foreground">{request.department}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Date</p>
          <p className="text-sm text-muted-foreground">{formatDate(request.date)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Hours</p>
          <p className="text-sm text-muted-foreground">
            <Clock className="mr-2 inline-block h-4 w-4" />
            {request.hours} hours
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Time</p>
          <p className="text-sm text-muted-foreground">
            {formatTime(request.start_time || "")} - {formatTime(request.end_time || "")}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Justification</p>
          <p className="text-sm text-muted-foreground">
            <FileText className="mr-2 inline-block h-4 w-4" />
            {request.justification_text || request.justification}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium leading-none">Status</p>
          <Badge className={getStatusColor(displayStatus as OvertimeStatus, 'solid')}>
            {isPendingVpApproval ? 'Pending VP Approval' : request.status}
          </Badge>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </CardFooter>
    </Card>
  );
}

export default RequestDetail;
